/**
 * Direct DB Handler
 * Direct Firestore access for all database operations
 *
 * This is the abstraction layer between processHandlers and Firestore
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteField,
  deleteDoc,
  onSnapshot,
  query,
  where,
  CollectionReference,
  Query
} from 'firebase/firestore'
import { auth, db } from '../../libs/Firebase'
import type { SprintStatus } from '../../../../pure-js/enums/SprintStatus'
import { Collections, CollectionGroups } from '../../../../pure-js/libs/Collections'
import { mapQueryResponse } from '../../../../pure-js/libs/FirebaseStorageClientBaseHelper'
import { mapCreateTeamRequestToTeam } from '../processHandlers/typeMappers'
import { ApiResponse } from '../types'
import { User, PartialUser } from '../../../../pure-js/types/User'
import { Team } from '../../../../pure-js/types/Team'
import { Session } from 'pure-js/types/Session'
import { Slot, SlotDataPoint } from '../../../../pure-js/types/Slot'
import { Questionnaire, QuestionnaireAnswer } from '../../../../pure-js/types/Questionnaire'
import { CreateTeamRequest, Id } from '../../../../pure-js/types/types'
import { Sprint, SprintActivity, SprintLaunch } from '../../../../pure-js/types/Sprint'
import { State } from '../../hooks/useAppState'
import { DemographicAnswers, DemographicQuestionId } from '../../../../pure-js/types/Demographics'

// ========================================
// USER FUNCTIONS
// ========================================

export const getUser = async (id: Id): Promise<ApiResponse<User | undefined>> => {
  try {
    const userDoc = await getDoc(doc(db, Collections.USERS, id))
    return {
      data: userDoc.exists() ? (mapQueryResponse(userDoc) as User) : undefined,
      success: true
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get user'
    return { data: undefined, success: false, error: message }
  }
}

export const updateUser = async (user: PartialUser): Promise<ApiResponse<void>> => {
  try {
    const docRef = doc(db, Collections.USERS, user.id)

    // Create update object, using deleteField() for undefined values
    const updateData: Record<string, unknown> = {}
    Object.keys(user).forEach((key) => {
      if (key === 'id') return // Skip id field
      if (user[key as keyof PartialUser] === undefined) {
        updateData[key] = deleteField()
      } else {
        updateData[key] = user[key as keyof PartialUser]
      }
    })

    await setDoc(docRef, updateData, { merge: true })
    return { data: undefined, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to update user'
    return { data: undefined, success: false, error: message }
  }
}

export const upsertUser = async (user: User): Promise<ApiResponse<void>> => {
  try {
    await setDoc(doc(db, Collections.USERS, user.id), user)
    return { data: undefined, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to upsert user'
    return { data: undefined, success: false, error: message }
  }
}

export const updateTeamIdForUser = async (team: string | null, user: Partial<User>): Promise<ApiResponse<void>> => {
  try {
    const userUpdate = { ...user, team }
    await updateDoc(doc(db, Collections.USERS, user.id!), userUpdate)
    return { data: undefined, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to update team for user'
    return { data: undefined, success: false, error: message }
  }
}

// ========================================
// TEAM FUNCTIONS
// ========================================

export const getTeam = async (id: Id): Promise<ApiResponse<Team | undefined>> => {
  try {
    const teamDoc = await getDoc(doc(db, Collections.TEAMS, id))
    return {
      data: teamDoc.exists() ? (mapQueryResponse(teamDoc) as Team) : undefined,
      success: true
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get team'
    return { data: undefined, success: false, error: message }
  }
}

export const getTeamsByIds = async (ids: string[]): Promise<ApiResponse<Team[]>> => {
  try {
    if (!ids || ids.length === 0) {
      return { data: [], success: true }
    }

    // Firestore 'in' queries are limited to 30 items, so we need to batch if necessary
    const batchSize = 30
    const batches: string[][] = []

    for (let i = 0; i < ids.length; i += batchSize) {
      batches.push(ids.slice(i, i + batchSize))
    }

    const allTeams: Team[] = []

    for (const batch of batches) {
      const teamsQuery = query(collection(db, Collections.TEAMS) as CollectionReference, where('id', 'in', batch))
      const snapshot = await getDocs(teamsQuery as Query)
      const batchTeams = snapshot.docs.map((doc) => mapQueryResponse(doc) as Team)
      allTeams.push(...batchTeams)
    }

    return { data: allTeams, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get teams by IDs'
    return { data: [], success: false, error: message }
  }
}

export const updateTeam = async (team: Team): Promise<ApiResponse<Team>> => {
  try {
    await setDoc(doc(db, Collections.TEAMS, team.id), team, { merge: true })
    return { data: team, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to update team'
    return { data: team, success: false, error: message }
  }
}

export const createTeam = async (team: CreateTeamRequest, state: State): Promise<ApiResponse<Team>> => {
  try {
    const teamId = `team-${Date.now()}`
    const newTeam = mapCreateTeamRequestToTeam(team, teamId, state)

    await setDoc(doc(db, Collections.TEAMS, teamId), newTeam)
    return { data: newTeam, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to create team'
    return { data: team as Team, success: false, error: message }
  }
}

// ========================================
// SESSION FUNCTIONS
// ========================================

export const getSessions = async (teamId: string): Promise<ApiResponse<Session[]>> => {
  try {
    const sessionsQuery = query(
      collection(db, CollectionGroups.SESSIONS) as CollectionReference,
      where('teamId', '==', teamId)
    )
    const snapshot = await getDocs(sessionsQuery as Query)
    const sessions = snapshot.docs.map((doc) => mapQueryResponse(doc) as Session)
    return { data: sessions, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get sessions'
    return { data: [], success: false, error: message }
  }
}

// ========================================
// SLOT FUNCTIONS
// ========================================

export const getSlotsForSessions = async (params: {
  sessionIds?: string[]
  teamId: string
}): Promise<ApiResponse<Slot[]>> => {
  try {
    const { sessionIds, teamId } = params

    if (sessionIds && sessionIds.length > 0) {
      // Query by session IDs
      const slotsQuery = query(
        collection(db, CollectionGroups.SLOTS) as CollectionReference,
        where('sessionId', 'in', sessionIds)
      )
      const snapshot = await getDocs(slotsQuery as Query)
      const slots = snapshot.docs.map((doc) => mapQueryResponse(doc) as Slot)
      return { data: slots, success: true }
    } else if (teamId) {
      // Query by team ID
      const slotsQuery = query(
        collection(db, CollectionGroups.SLOTS) as CollectionReference,
        where('teamId', '==', teamId)
      )
      const snapshot = await getDocs(slotsQuery as Query)
      const slots = snapshot.docs.map((doc) => mapQueryResponse(doc) as Slot)
      return { data: slots, success: true }
    }

    return { data: [], success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get slots'
    return { data: [], success: false, error: message }
  }
}

export const getUserDataPointMapsForSlots = async (params: {
  slotIds: string[]
  teamId?: string
  sessionId?: string
}): Promise<ApiResponse<SlotDataPoint[]>> => {
  try {
    // Query data points collection for the given slot IDs
    if (params.slotIds.length === 0) {
      return { data: [], success: true }
    }

    const dataPointsQuery = query(
      collection(db, CollectionGroups.DATA_POINTS) as CollectionReference,
      where('slotId', 'in', params.slotIds)
    )
    const snapshot = await getDocs(dataPointsQuery as Query)
    const dataPoints = snapshot.docs.map((doc) => mapQueryResponse(doc) as SlotDataPoint)
    return { data: dataPoints, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get data points'
    return { data: [], success: false, error: message }
  }
}

// ========================================
// QUESTIONNAIRE FUNCTIONS
// ========================================

export const getQuestionnaire = async (params: { id: string }): Promise<ApiResponse<Questionnaire | undefined>> => {
  try {
    const questionnaireDoc = await getDoc(doc(db, Collections.QUESTIONARES, params.id))
    if (!questionnaireDoc.exists()) {
      return { data: undefined, success: true }
    }
    return { data: questionnaireDoc.data() as Questionnaire, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get questionnaire'
    return { data: undefined, success: false, error: message }
  }
}

export const getQuestionnaireAnswer = async (params: {
  questionnaireId: string
  questionId: string
}): Promise<ApiResponse<QuestionnaireAnswer | undefined>> => {
  try {
    // Note: Collections.QUESTIONARES has the typo from backend
    const answerDoc = await getDoc(
      doc(db, Collections.QUESTIONARES, params.questionnaireId, 'answers', params.questionId)
    )
    if (!answerDoc.exists()) {
      return { data: undefined, success: true }
    }
    return { data: answerDoc.data() as QuestionnaireAnswer, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get questionnaire answer'
    return { data: undefined, success: false, error: message }
  }
}

export const updateQuestionnaire = async (questionnaire: Questionnaire): Promise<ApiResponse<Questionnaire>> => {
  try {
    await setDoc(doc(db, Collections.QUESTIONARES, questionnaire.id), questionnaire, { merge: true })
    return { data: questionnaire, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to update questionnaire'
    return { data: questionnaire, success: false, error: message }
  }
}

export const updateQuestionareAnswer = async (
  answer: QuestionnaireAnswer,
  questionnaireId: string
): Promise<ApiResponse<QuestionnaireAnswer>> => {
  try {
    // Note: Function name preserves typo for compatibility
    await setDoc(doc(db, Collections.QUESTIONARES, questionnaireId, 'answers', answer.questionId), answer, {
      merge: true
    })
    return { data: answer, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to update answer'
    return { data: answer, success: false, error: message }
  }
}

export const getQuestionnaireAnswers = async (
  questionnaireId: string
): Promise<ApiResponse<Record<string, QuestionnaireAnswer>>> => {
  try {
    const answersCollectionRef = collection(db, Collections.QUESTIONARES, questionnaireId, 'answers')
    const answersSnapshot = await getDocs(answersCollectionRef)

    const answers: Record<string, QuestionnaireAnswer> = {}
    answersSnapshot.forEach((doc) => {
      answers[doc.id] = doc.data() as QuestionnaireAnswer
    })

    return { data: answers, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get questionnaire answers'
    return { data: {}, success: false, error: message }
  }
}

export const deleteQuestionnaireAnswers = async (questionnaireId: string): Promise<ApiResponse<void>> => {
  try {
    const answersCollectionRef = collection(db, Collections.QUESTIONARES, questionnaireId, 'answers')
    const answersSnapshot = await getDocs(answersCollectionRef)

    const deletions: Promise<void>[] = []
    answersSnapshot.forEach((answerDoc) => {
      deletions.push(deleteDoc(answerDoc.ref))
    })

    await Promise.all(deletions)

    return { data: undefined, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to delete questionnaire answers'
    return { data: undefined, success: false, error: message }
  }
}

export const deleteQuestionnaire = async (questionnaireId: string): Promise<ApiResponse<void>> => {
  try {
    await deleteDoc(doc(db, Collections.QUESTIONARES, questionnaireId))
    return { data: undefined, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to delete questionnaire'
    return { data: undefined, success: false, error: message }
  }
}

// ========================================
// DEMOGRAPHIC FUNCTIONS
// ========================================

export const saveDemographicAnswer = async (
  userId: string,
  questionId: string,
  answer: number,
  otherText?: string | null
): Promise<ApiResponse<undefined>> => {
  try {
    const payload: {
      answer: number
      timeStamp: number
      otherText?: string | ReturnType<typeof deleteField>
    } = { answer, timeStamp: Date.now() }

    if (otherText === null) {
      payload.otherText = deleteField()
    } else if (typeof otherText === 'string') {
      payload.otherText = otherText
    }

    await setDoc(doc(db, Collections.USER_DEMOGRAPHICS, userId, 'answers', questionId), payload, { merge: true })
    return { data: undefined, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to save demographic answer'
    return { data: undefined, success: false, error: message }
  }
}

export const deleteDemographicAnswer = async (userId: string, questionId: string): Promise<ApiResponse<undefined>> => {
  try {
    await deleteDoc(doc(db, Collections.USER_DEMOGRAPHICS, userId, 'answers', questionId))
    return { data: undefined, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to delete demographic answer'
    return { data: undefined, success: false, error: message }
  }
}

export const getDemographicAnswersCount = async (userId: string): Promise<ApiResponse<number>> => {
  try {
    const answersRef = collection(db, Collections.USER_DEMOGRAPHICS, userId, 'answers')
    const snapshot = await getDocs(answersRef)
    return { data: snapshot.size, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get demographic answers count'
    return { data: 0, success: false, error: message }
  }
}

export const getDemographicAnswers = async (userId: string): Promise<ApiResponse<DemographicAnswers>> => {
  try {
    const answersRef = collection(db, Collections.USER_DEMOGRAPHICS, userId, 'answers')
    const snapshot = await getDocs(answersRef)
    const answers: DemographicAnswers = {}
    snapshot.docs.forEach((d) => {
      const data = d.data()
      if (typeof data?.answer === 'number') {
        answers[d.id as DemographicQuestionId] = {
          answer: data.answer,
          timeStamp: typeof data?.timeStamp === 'number' ? data.timeStamp : Date.now(),
          ...(typeof data?.otherText === 'string' ? { otherText: data.otherText } : {})
        }
      }
    })
    return { data: answers, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get demographic answers'
    return { data: {}, success: false, error: message }
  }
}

// ========================================
// SPRINT FUNCTIONS
// ========================================

export const getSprints = async (): Promise<ApiResponse<Sprint[]>> => {
  try {
    const sprintsSnapshot = await getDocs(collection(db, Collections.SPRINTS))
    const sprints = sprintsSnapshot.docs.map((doc) => mapQueryResponse(doc) as Sprint)
    return { data: sprints, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get sprints'
    return { data: [], success: false, error: message }
  }
}

export const getActivities = async (sprintId: string): Promise<ApiResponse<SprintActivity[]>> => {
  try {
    const activitiesQuery = query(
      collection(db, CollectionGroups.ACTIVITIES) as CollectionReference,
      where('sprintId', '==', sprintId)
    )
    const snapshot = await getDocs(activitiesQuery as Query)
    const activities = snapshot.docs.map((doc) => mapQueryResponse(doc) as SprintActivity)
    return { data: activities, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get activities'
    return { data: [], success: false, error: message }
  }
}

export const getSprintReports = async (): Promise<ApiResponse<Record<string, unknown>[]>> => {
  try {
    const reportsSnapshot = await getDocs(collection(db, Collections.SPRINT_REPORTS))
    const reports = reportsSnapshot.docs.map((doc) => mapQueryResponse(doc) as Record<string, unknown>)
    return { data: reports, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get sprint reports'
    return { data: [], success: false, error: message }
  }
}

export const upsertSprint = async (sprint: Sprint): Promise<ApiResponse<Sprint>> => {
  try {
    await setDoc(doc(db, Collections.SPRINTS, sprint.id), sprint, { merge: true })
    return { data: sprint, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to upsert sprint'
    return { data: sprint, success: false, error: message }
  }
}

export const upsertActivity = async (
  activity: SprintActivity,
  sprint: Sprint
): Promise<ApiResponse<SprintActivity>> => {
  try {
    const activityData = { ...activity, sprintId: sprint.id }
    await setDoc(doc(db, CollectionGroups.ACTIVITIES, activity.id), activityData, { merge: true })
    return { data: activityData, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to upsert activity'
    return { data: activity, success: false, error: message }
  }
}

export const upsertSprintReport = async (
  sprintReport: Record<string, unknown> & { id: string }
): Promise<ApiResponse<Record<string, unknown>>> => {
  try {
    await setDoc(doc(db, Collections.SPRINT_REPORTS, sprintReport.id), sprintReport, { merge: true })
    return { data: sprintReport, success: true }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to upsert sprint report'
    return { data: sprintReport, success: false, error: message }
  }
}

export type SprintLaunchSnapshot = { sprintStatus: SprintStatus; launch?: SprintLaunch }

// The launch job lives on the sprint document, so the wizard follows it live instead of asking a function.
export const observeSprintLaunch = (
  sprintId: string,
  onChange: (snapshot: SprintLaunchSnapshot) => void,
  onError: (error: Error) => void
): (() => void) =>
  onSnapshot(
    doc(db, Collections.SPRINTS, sprintId),
    (snapshot) => {
      const sprint = snapshot.data() as Pick<Sprint, 'status' | 'launch'> | undefined
      if (sprint) onChange({ sprintStatus: sprint.status, launch: sprint.launch })
    },
    onError
  )

export type GameSessionSnapshot = Pick<Session, 'status' | 'forceClosedAt'>

const CLAIMS_LISTEN_RETRY_MS = 30_000 // 30 s
const CLAIMS_LISTEN_MAX_ATTEMPTS = 10

type ListenerErrorHandler = (error: Error) => void

// Team and sprint access live in the ID token's claims; a player added to a team after signing in holds a token without it until refreshed.
const refreshIdTokenClaims = async (): Promise<void> => {
  await auth?.currentUser?.getIdToken(true)
}

const isPermissionDenied = (error: Error): boolean => (error as { code?: string }).code === 'permission-denied'

// Wraps a Firestore listener whose rules read the token's claims: a denied read is retried on a refreshed token, at once the
// first time and then every CLAIMS_LISTEN_RETRY_MS, since the claims themselves can lag a launch by minutes.
export const listenWithFreshClaims = (
  subscribe: (onListenError: ListenerErrorHandler) => () => void,
  onError: ListenerErrorHandler
): (() => void) => {
  let unsubscribe: (() => void) | undefined
  let retryTimer: ReturnType<typeof setTimeout> | undefined
  let stopped = false

  const listen = (attempt: number) => {
    if (stopped) return
    unsubscribe = subscribe((error) => {
      unsubscribe = undefined
      if (!isPermissionDenied(error) || attempt >= CLAIMS_LISTEN_MAX_ATTEMPTS) {
        onError(error)
        return
      }
      const delayMs = attempt === 1 ? 0 : CLAIMS_LISTEN_RETRY_MS
      retryTimer = setTimeout(() => {
        void refreshIdTokenClaims()
          .catch(onError)
          .then(() => listen(attempt + 1))
      }, delayMs)
    })
  }

  listen(1)
  return () => {
    stopped = true
    if (retryTimer) clearTimeout(retryTimer)
    unsubscribe?.()
  }
}

// The game page follows its own session document; an operator's Back Office close is the one outside signal that ends a running game.
export const observeGameSession = (
  teamId: string,
  sessionId: string,
  onChange: (session: GameSessionSnapshot | undefined) => void,
  onError: ListenerErrorHandler
): (() => void) =>
  listenWithFreshClaims(
    (onListenError) =>
      onSnapshot(
        doc(db, Collections.TEAMS, teamId, CollectionGroups.SESSIONS, sessionId),
        (snapshot) => onChange(snapshot.data() as GameSessionSnapshot | undefined),
        onListenError
      ),
    onError
  )
